import React, { createContext, useContext, useId, useState } from 'react'
import { ChevronDown, ChevronUp } from 'components/icons'

// Coordinates a set of Collapses so they can behave as an accordion (only one open at a time).
interface CollapseGroupContextValue {
  isOpen: (id: string) => boolean
  toggle: (id: string) => void
}

const CollapseGroupContext = createContext<CollapseGroupContextValue | null>(null)

interface CollapseGroupProps {
  allowOne?: boolean
  children: React.ReactNode
}

// Wrap sibling <Collapse>s to coordinate their open state. Optional — a Collapse works standalone too.
export const CollapseGroup: React.FC<CollapseGroupProps> = ({ allowOne = false, children }) => {
  // React Compiler mishandles context providers (see CLAUDE.md), so opt this one out.
  'use no memo'
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())

  const value: CollapseGroupContextValue = {
    isOpen: (id) => openIds.has(id),
    toggle: (id) =>
      setOpenIds((prev) => {
        const wasOpen = prev.has(id)
        if (allowOne) return wasOpen ? new Set() : new Set([id])
        const next = new Set(prev)
        if (wasOpen) next.delete(id)
        else next.add(id)
        return next
      })
  }

  return <CollapseGroupContext.Provider value={value}>{children}</CollapseGroupContext.Provider>
}

interface CollapseProps {
  text: string
  subtext?: string
  children: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onToggle?: () => void
  headerClassName?: string
  bodyClassName?: string
  dataTestId?: string
}

// A collapsible section: a clickable grey header row with a chevron that swaps up (open) / down
// (closed) and a body that shows only when open. Mirrors the Integrations (import) menu's accordion
// rows. Open state comes from `open` (controlled), else the enclosing CollapseGroup, else itself.
const Collapse: React.FC<CollapseProps> = ({
  text,
  subtext,
  children,
  defaultOpen = false,
  open: openProp,
  onToggle,
  headerClassName = 'py-2.5 px-5',
  bodyClassName = 'py-2.5',
  dataTestId
}) => {
  const group = useContext(CollapseGroupContext)
  const id = useId()
  const [selfOpen, setSelfOpen] = useState<boolean>(defaultOpen)

  const open = openProp !== undefined ? openProp : group ? group.isOpen(id) : selfOpen

  const handleToggle = (): void => {
    if (onToggle) onToggle()
    else if (group) group.toggle(id)
    else setSelfOpen((prev) => !prev)
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        data-testid={dataTestId}
        className={`flex items-center bg-surface-sunken cursor-pointer border-t border-divider ${headerClassName}`}
        onClick={handleToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggle()
          }
        }}
      >
        <span className="font-bold">{text}</span>
        {subtext && <span className="ml-2 text-muted text-[90%]">{subtext}</span>}
        <span className="ml-auto flex text-muted">{open ? <ChevronUp /> : <ChevronDown />}</span>
      </div>
      {open && <div className={bodyClassName}>{children}</div>}
    </div>
  )
}

export default Collapse
